
import { openDB, DBSchema, IDBPDatabase } from 'idb';

interface UserDB extends DBSchema {
  'users': {
    key: string; // userId (Google ID)
    value: {
      userId: string;
      email: string;
      displayName: string;
      photoURL: string;
      tier: 'free' | 'premium' | 'elite';
      createdAt: number;
      lastLoginAt: number;
      trialEndsAt?: number;
      subscriptionEndsAt?: number;
      devAccess: boolean; // Special flag for dev coupon users
      couponUsed?: string;
      stats: {
        xp: number;
        level: number;
        streak: number;
        lastActiveDate: string;
        exercisesCompleted: number;
        totalTimeSpent: number;
        dailyExerciseCount: number;
        lastExerciseDate: string;
        writingEvalsUsed: number;
        perfectReadings: number;
        maxReadingSpeed: number;
        vocabMastered: number;
      };
      achievements: string[];
      preferences: {
        difficulty: 'easy' | 'medium' | 'hard';
        dailyGoal: number;
        soundEnabled: boolean;
        theme: 'dark' | 'light';
      };
    };
    indexes: { 'email': string };
  };
  'sessions': {
    key: string;
    value: {
      userId: string;
      token: string;
      expiresAt: number;
    };
  };
}

const DEV_COUPON = process.env.VITE_DEV_COUPON_CODE || 'TEDTALKS_DEV_2025_ULTRA';
const ALT_DEV_COUPON = 'DTALKS_DEV_2025_ULTRA';

export class AuthService {
  private db: IDBPDatabase<UserDB> | null = null;
  private currentUser: any = null;

  async initialize() {
    this.db = await openDB<UserDB>('ted-talks-dz-users', 3, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains('users')) {
          const userStore = db.createObjectStore('users', { keyPath: 'userId' });
          userStore.createIndex('email', 'email', { unique: true });
        }
        if (!db.objectStoreNames.contains('sessions')) {
          db.createObjectStore('sessions', { keyPath: 'userId' });
        }
      }
    });

    const sessionUserId = sessionStorage.getItem('ielts_user_id');
    if (sessionUserId) {
      const user = await this.getUserById(sessionUserId);
      if (user) {
        this.currentUser = user;
        await this.updateLastLogin(sessionUserId);
      }
    }
  }

  async signInWithGoogle(googleUser: any): Promise<string> {
    if (!this.db) await this.initialize();

    const userId = googleUser.sub || googleUser.id;
    const email = googleUser.email;
    const displayName = googleUser.name;
    const photoURL = googleUser.picture;

    let user = await this.getUserById(userId);

    if (!user) {
      user = {
        userId,
        email,
        displayName,
        photoURL,
        tier: 'free',
        createdAt: Date.now(),
        lastLoginAt: Date.now(),
        trialEndsAt: Date.now() + (7 * 24 * 60 * 60 * 1000),
        devAccess: false,
        stats: {
          xp: 0,
          level: 1,
          streak: 0,
          lastActiveDate: new Date().toISOString().split('T')[0],
          exercisesCompleted: 0,
          totalTimeSpent: 0,
          dailyExerciseCount: 0,
          lastExerciseDate: '',
          writingEvalsUsed: 0,
          perfectReadings: 0,
          maxReadingSpeed: 0,
          vocabMastered: 0
        },
        achievements: [],
        preferences: {
          difficulty: 'medium',
          dailyGoal: 30,
          soundEnabled: true,
          theme: 'dark'
        }
      };
      await this.db?.put('users', user);
    } else {
      await this.updateLastLogin(userId);
    }

    sessionStorage.setItem('ielts_user_id', userId);
    this.currentUser = user;
    return userId;
  }

  async signOut() {
    sessionStorage.removeItem('ielts_user_id');
    this.currentUser = null;
  }

  async getUserById(userId: string) {
    if (!this.db) await this.initialize();
    return await this.db?.get('users', userId);
  }

  async getCurrentUser() {
    if (this.currentUser) return this.currentUser;
    const userId = sessionStorage.getItem('ielts_user_id');
    if (!userId) return null;
    this.currentUser = await this.getUserById(userId);
    return this.currentUser;
  }

  async updateUser(userId: string, updates: Partial<any>) {
    const user = await this.getUserById(userId);
    if (!user) return;

    const mergedStats = updates.stats ? { ...user.stats, ...updates.stats } : user.stats;
    const mergedPreferences = updates.preferences ? { ...user.preferences, ...updates.preferences } : user.preferences;

    if (updates['stats.xp'] !== undefined) mergedStats.xp = updates['stats.xp'];
    if (updates['stats.level'] !== undefined) mergedStats.level = updates['stats.level'];
    if (updates['stats.dailyExerciseCount'] !== undefined) mergedStats.dailyExerciseCount = updates['stats.dailyExerciseCount'];
    if (updates['stats.exercisesCompleted'] !== undefined) mergedStats.exercisesCompleted = updates['stats.exercisesCompleted'];
    if (updates['stats.lastActiveDate'] !== undefined) mergedStats.lastActiveDate = updates['stats.lastActiveDate'];
    if (updates['stats.streak'] !== undefined) mergedStats.streak = updates['stats.streak'];

    const updatedUser = {
      ...user,
      ...updates,
      stats: mergedStats,
      preferences: mergedPreferences
    };

    delete updatedUser['stats.xp'];
    delete updatedUser['stats.level'];
    delete updatedUser['stats.dailyExerciseCount'];
    delete updatedUser['stats.exercisesCompleted'];
    delete updatedUser['stats.lastActiveDate'];
    delete updatedUser['stats.streak'];

    await this.db?.put('users', updatedUser);
    if (this.currentUser?.userId === userId) {
      this.currentUser = updatedUser;
    }
    window.dispatchEvent(new CustomEvent('user-stats-updated', { detail: updatedUser }));
  }

  async updateLastLogin(userId: string) {
    await this.updateUser(userId, { lastLoginAt: Date.now() });
  }

  async upgradeTier(userId: string, tier: 'premium' | 'elite', duration: number) {
    const user = await this.getUserById(userId);
    if (!user) return;
    await this.db?.put('users', {
      ...user,
      tier,
      subscriptionEndsAt: Date.now() + duration
    });
  }

  async loginWithDevCode(code: string): Promise<{ success: boolean; userId?: string; message: string }> {
    const normalizedCode = code.trim().toUpperCase();
    if (normalizedCode !== DEV_COUPON && normalizedCode !== ALT_DEV_COUPON) {
      return { success: false, message: 'Invalid access code' };
    }

    if (!this.db) await this.initialize();
    const devUserId = 'dev_admin_001';
    let user = await this.getUserById(devUserId);

    if (!user) {
      user = {
        userId: devUserId,
        email: 'admin@tedtalks.dz',
        displayName: 'System Commander',
        photoURL: 'https://ui-avatars.com/api/?name=System+Commander&background=C5A059&color=1c1917',
        tier: 'elite',
        createdAt: Date.now(),
        lastLoginAt: Date.now(),
        devAccess: true,
        couponUsed: normalizedCode,
        subscriptionEndsAt: 8640000000000000,
        stats: {
          xp: 99999,
          level: 100,
          streak: 365,
          lastActiveDate: new Date().toISOString().split('T')[0],
          exercisesCompleted: 5000,
          totalTimeSpent: 100000,
          dailyExerciseCount: 0,
          lastExerciseDate: '',
          writingEvalsUsed: 0,
          perfectReadings: 500,
          maxReadingSpeed: 1200,
          vocabMastered: 8000
        },
        achievements: ['first_blood', 'streak_7', 'streak_30', 'hundred_club', 'speed_demon', 'perfect_reading', 'vocab_1000'],
        preferences: {
          difficulty: 'hard',
          dailyGoal: 50,
          soundEnabled: true,
          theme: 'dark'
        }
      };
      await this.db?.put('users', user);
    } else {
      await this.updateUser(devUserId, {
        tier: 'elite',
        devAccess: true,
        lastLoginAt: Date.now()
      });
    }

    sessionStorage.setItem('ielts_user_id', devUserId);
    this.currentUser = user;
    return { success: true, userId: devUserId, message: 'ACCESS GRANTED: COMMANDER LEVEL' };
  }

  async redeemCoupon(userId: string, couponCode: string): Promise<{ success: boolean; message: string; tier?: string }> {
    const user = await this.getUserById(userId);
    if (!user) return { success: false, message: 'User not found' };

    const normalizedCode = couponCode.trim().toUpperCase();
    if (user.couponUsed === normalizedCode) return { success: false, message: 'Coupon already redeemed' };

    if (normalizedCode === DEV_COUPON || normalizedCode === ALT_DEV_COUPON) {
      const updatedUser = {
        ...user,
        tier: 'elite' as const,
        devAccess: true,
        couponUsed: normalizedCode,
        subscriptionEndsAt: Date.now() + (100 * 365 * 24 * 60 * 60 * 1000)
      };
      await this.db?.put('users', updatedUser);
      this.currentUser = updatedUser;
      window.dispatchEvent(new CustomEvent('user-stats-updated', { detail: updatedUser }));
      return { success: true, message: '🚀 DEVELOPER ACCESS GRANTED - All features unlocked!', tier: 'elite' };
    }

    if (normalizedCode.startsWith('TRIAL_')) {
      await this.db?.put('users', {
        ...user,
        tier: 'premium',
        couponUsed: normalizedCode,
        subscriptionEndsAt: Date.now() + (30 * 24 * 60 * 60 * 1000)
      });
      return { success: true, message: '🎉 30-Day Premium Trial Activated!', tier: 'premium' };
    }
    return { success: false, message: 'Invalid coupon code' };
  }

  hasTier(user: any, minTier: 'free' | 'premium' | 'elite'): boolean {
    if (!user) return false;
    if (user.devAccess) return true;
    const tierHierarchy = { free: 0, premium: 1, elite: 2 };
    const userTierLevel = tierHierarchy[user.tier as 'free' | 'premium' | 'elite'];
    const requiredTierLevel = tierHierarchy[minTier];
    if (user.subscriptionEndsAt && Date.now() > user.subscriptionEndsAt) return minTier === 'free';
    if (user.trialEndsAt && Date.now() < user.trialEndsAt && userTierLevel < requiredTierLevel) return requiredTierLevel <= 1;
    return userTierLevel >= requiredTierLevel;
  }

  getGuestUser() {
    return {
      userId: 'guest_commander',
      email: 'guest@tedtalks.dz',
      displayName: 'Guest Commander',
      photoURL: 'https://ui-avatars.com/api/?name=Guest+Commander&background=1c1917&color=C5A059',
      tier: 'free',
      isGuest: true,
      stats: {
        xp: 0,
        level: 1,
        streak: 0,
        lastActiveDate: new Date().toISOString().split('T')[0],
        exercisesCompleted: 0
      },
      preferences: {
        difficulty: 'medium',
        theme: 'dark'
      }
    };
  }
}

export const authService = new AuthService();
